import { useQuery } from "@tanstack/react-query";
import type { Variable } from "@/domains/templates/types";
import {
  getStepVariableSources,
  getStepVariableBindingReadiness,
} from "@/domains/templates/step-variable-bindings";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { formatVariableDefault } from "../lib/format-variable-default";
export function TemplateVariableBindingSummary({
  variable,
  templateId,
  stepId,
  parentVariables,
}: {
  variable: Variable;
  templateId: string;
  stepId: string;
  parentVariables: Variable[];
}) {
  const needsSteps = ["step_variable", "step_output"].includes(
    variable.binding_kind ?? "",
  );
  const steps = useQuery({
    ...templateBuilderQueries.steps(templateId),
    enabled: needsSteps,
  });
  const child = useQuery({
    ...templateBuilderQueries.detail(variable.binding_template_id ?? ""),
    enabled:
      variable.binding_kind === "template" && !!variable.binding_template_id,
  });
  let summary: string;
  switch (variable.binding_kind) {
    case "template":
      summary = child.isPending
        ? "Loading reference…"
        : child.data?.template_setting?.display_name ||
          child.data?.analysis_name ||
          "Missing reference";
      break;
    case "step_output": {
      const source = steps.data?.find(
        (step) => step.id === variable.binding_source_step_id,
      );
      const consumer = steps.data?.find((step) => step.id === stepId);
      const valid = source && consumer && source.order < consumer.order;
      const groups = getStepVariableSources(
        steps.data ?? [],
        stepId,
        variable.binding_source_step_id ?? "__none__",
        "array",
      );
      const selected = groups
        .flatMap((group) => [...group.inputs, ...group.outputs])
        .find((v) => v.id === variable.binding_source_variable_id);
      summary = !variable.binding_source_step_id
        ? "Incomplete: choose an earlier section"
        : !valid
          ? "Missing reference"
          : `${source.name} → ${selected?.variable.variable_name_frontend || selected?.variable.variable_name || "Select source variable"}${!selected || getStepVariableBindingReadiness(selected, variable.binding_path, "array") === "incomplete" ? " · Incomplete" : ""}`;
      break;
    }
    case "step_variable": {
      const source = getStepVariableSources(
        steps.data ?? [],
        stepId,
        undefined,
        variable.variable_type,
      )
        .flatMap((group) => [...group.inputs, ...group.outputs])
        .find((source) => source.id === variable.binding_source_variable_id);
      summary = source
        ? `${source.label}${variable.binding_path ? ` ${variable.binding_path}` : ""}${getStepVariableBindingReadiness(source, variable.binding_path, variable.variable_type) === "incomplete" ? " · Incomplete" : ""}`
        : "Missing reference";
      break;
    }
    case "parent_variable": {
      const source = parentVariables.find(
        (v) => v.variable_id === variable.binding_source_variable_id,
      );
      summary = `Parent: ${source?.variable_name_frontend || source?.variable_name || "Missing reference"}`;
      break;
    }
    case "constant":
      summary = `Fixed: ${variable.variable_default === null ? "null" : formatVariableDefault(variable.variable_default)}`;
      break;
    case "item":
      summary = `Item: ${variable.binding_path || "whole item"}`;
      break;
    case "target_default":
      summary = "Child default";
      break;
    default:
      summary = "Incomplete: choose a source or value";
  }
  return <span className="text-xs text-muted-foreground">{summary}</span>;
}
